import React, { useEffect, useRef, useState } from "react";

const ZEGO_CDN =
  "https://unpkg.com/@zegocloud/zego-uikit-prebuilt/zego-uikit-prebuilt.js";

const ZegoRoom = ({ roomID, userID, userName, height = "520px" }) => {
  const containerRef = useRef(null);
  const zpRef = useRef(null);
  const [zegoReady, setZegoReady] = useState(!!window.ZegoUIKitPrebuilt);
  const [joinError, setJoinError] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (window.ZegoUIKitPrebuilt) {
      setZegoReady(true);
      return;
    }
    const existing = document.querySelector(`script[src="${ZEGO_CDN}"]`);
    if (existing) {
      const onLoad = () => setZegoReady(true);
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }
    const script = document.createElement("script");
    script.src = ZEGO_CDN;
    script.async = true;
    script.onload = () => setZegoReady(true);
    script.onerror = () => {
      console.error("ZEGOCLOUD CDN failed to load");
      setJoinError("Failed to load video call library. Please check your internet connection and try again.");
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!zegoReady || !containerRef.current || !roomID) {
      if (zegoReady && roomID) setJoining(false);
      return;
    }
    const ZegoUIKitPrebuilt = window.ZegoUIKitPrebuilt;
    if (!ZegoUIKitPrebuilt) return;

    // Check for secure context
    if (!window.isSecureContext) {
      setJoinError(
        `Video call requires a secure connection (HTTPS or "localhost"). This page is loaded over an insecure origin (${window.location.origin}), so the browser blocks camera/microphone access.`
      );
      return;
    }

    setJoinError("");
    setJoining(true);

    try {
      const appID = Number(process.env.REACT_APP_ZEGO_APP_ID);
      const serverSecret = process.env.REACT_APP_ZEGO_SERVER_SECRET;

      if (!appID || !serverSecret) {
        throw new Error("ZEGO App ID or Server Secret is not configured. Please check your .env file.");
      }

      const kitToken = ZegoUIKitPrebuilt.generateKitTokenForTest(
        appID,
        serverSecret,
        roomID,
        userID || String(Date.now()),
        userName || "User"
      );

      const zp = ZegoUIKitPrebuilt.create(kitToken);
      zpRef.current = zp;

      const joinPromise = zp.joinRoom({
        container: containerRef.current,
        scenario: { mode: ZegoUIKitPrebuilt.VideoConference },
        showScreenSharingButton: true,
        showTurnOffRemoteCameraButton: true,
        showTurnOffRemoteMicrophoneButton: true,
        showMyCameraToggleButton: true,
        showMyMicrophoneToggleButton: true,
        showAudioVideoSettingsButton: true,
        showTextChat: true,
        showUserList: true,
        maxUsers: 6,
        turnOnCameraWhenJoining: true,
        turnOnMicrophoneWhenJoining: true,
        showPreJoinView: false,
        showLeavingView: false,
      });

      if (joinPromise && typeof joinPromise.then === 'function') {
        joinPromise.then(() => {
          console.log("Successfully joined ZEGO room:", roomID);
          setJoining(false);
        }).catch((err) => {
          console.error("ZEGOCLOUD joinRoom failed:", err);
          setJoinError(err?.message || "Failed to start the video call. Check camera/microphone permissions and network connection.");
          setJoining(false);
        });
      } else {
        console.log("ZEGOCLOUD joinRoom called (no promise returned)");
        setJoining(false);
      }
    } catch (err) {
      console.error("Error setting up ZEGO room:", err);
      setJoinError(err.message || "Failed to initialize video call.");
      setJoining(false);
    }

    return () => {
      const zp = zpRef.current;
      zpRef.current = null;
      if (zp) {
        // Delay destroy so in-flight WebSocket connections finish their
        // handshake before being closed — prevents the "WebSocket closed
        // before connection established" warning from StrictMode double-mount.
        setTimeout(() => {
          try { zp.destroy(); } catch (_) {}
        }, 300);
      }
    };
  }, [zegoReady, roomID, userID, userName]);

  if (!zegoReady) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height,
        background: "#f0f4f8",
        borderRadius: 8,
        color: "#555",
        fontSize: 14,
        gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>⏳</span> Loading video call library...
      </div>
    );
  }

  if (joinError) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height,
        background: "#fff3e0",
        border: "1px solid #ffb74d",
        borderRadius: 8,
        color: "#e65100",
        fontSize: 13,
        textAlign: "center",
        padding: 24,
        gap: 8,
      }}>
        <span style={{ fontSize: 24 }}>⚠️</span>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Video Call Error</div>
          <div>{joinError}</div>
        </div>
      </div>
    );
  }

  if (joining) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height,
        background: "#e3f2fd",
        borderRadius: 8,
        color: "#1976d2",
        fontSize: 14,
        gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>🔄</span> Joining video room...
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: "100%", height }} />;
};

export default ZegoRoom;
